/**
 * Client-side logger. Console always; warn/error optionally relayed to
 * a `logClient` callable for Cloud Logging. Until that callable lands
 * server-side, the cloud path is a stub — the API stays stable so
 * call sites don't need changes.
 */

import { auth } from './firebase';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
interface LogOptions {
  sendToCloud?: boolean;
}

function logToConsole(level: LogLevel, message: string, ctx: Record<string, unknown>): void {
  const method = level === 'debug' ? 'log' : level;
  console[method](`[${level.toUpperCase()}] ${message}`, ctx);
}

function sendToCloud(_level: LogLevel, _message: string, _ctx: Record<string, unknown>): void {
  // No-op until the logClient callable ships. Kept here so call sites
  // don't change when it does.
}

function makeLogFn(level: LogLevel, sendByDefault: boolean) {
  return (
    message: string,
    context: Record<string, unknown> = {},
    options: LogOptions = {},
  ): void => {
    const enriched = { ...context, userId: auth.currentUser?.uid ?? null };
    logToConsole(level, message, enriched);
    if (options.sendToCloud ?? sendByDefault) {
      sendToCloud(level, message, enriched);
    }
  };
}

function startTimer(): () => number {
  const t0 = performance.now();
  return () => Math.round(performance.now() - t0);
}

export const log = {
  debug: makeLogFn('debug', false),
  info: makeLogFn('info', false),
  warn: makeLogFn('warn', true),
  error: makeLogFn('error', true),
  startTimer,
};
